import React, { useEffect, useState } from 'react';
import { LoadPanel } from 'devextreme-react/load-panel';
import ScrollView from 'devextreme-react/scroll-view';
import DataGrid, { Column } from 'devextreme-react/data-grid';
import { Chart, Series, ArgumentAxis, ValueAxis, Legend, Tooltip, CommonSeriesSettings } from 'devextreme-react/chart';
import { useAuth } from '../../contexts/auth';
import { getFullDashboard } from '../../api/dashboard';
import { useNavigate } from 'react-router-dom';
import './analytics-dashboard.scss';

const MONTH_NAMES = ['Jan', 'Feb', 'Mrt', 'Apr', 'Mei', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dec'];

const STATUS_COLORS: Record<string, string> = {
    New: '#2196f3',
    InProgress: '#ff9800',
    Completed: '#4caf50',
    Pending: '#9c27b0',
    Cancelled: '#9e9e9e',
    Deferred: '#795548',
};

export const AnalyticsDashboard = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [data, setData] = useState<any>(null);
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (user?.token) {
            getFullDashboard(user.token)
                .then((result) => {
                    setData(result);
                    setIsLoading(false);
                })
                .catch(() => {
                    setIsLoading(false);
                });
        }
    }, [user?.token]);

    const chartData = (data?.serviceOverview || []).map((item: any) => ({
        month: MONTH_NAMES[(item.Month || 1) - 1],
        count: item.Count
    }));

    const taskStatusData = data ? [
        { status: 'Nieuw', count: data.tasks.statusNew, color: STATUS_COLORS.New },
        { status: 'Bezig', count: data.tasks.inProgress, color: STATUS_COLORS.InProgress },
        { status: 'Afgerond', count: data.tasks.completed, color: STATUS_COLORS.Completed },
        { status: 'Wachtend', count: data.tasks.pending, color: STATUS_COLORS.Pending },
    ].filter(d => d.count > 0) : [];

    const onTaskRowClick = (e: any) => {
        if (e.data?.Oid) {
            navigate('/worktask-details', { state: { workTaskOid: e.data.Oid, backTo: 'analyticsDashboard', relationOid: '' } });
        }
    };

    return (
        <ScrollView className='view-wrapper-scroll'>
            <div className='dashboard-container'>
                <div className='dashboard-header'>
                    <h2 className='dashboard-title'>Dashboard</h2>
                    <span className='dashboard-date'>{new Date().toLocaleDateString('nl-NL', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
                </div>

                {/* KPI Row - Contracts */}
                <div className='kpi-section'>
                    <h3 className='section-title'>Contracten</h3>
                    <div className='kpi-row'>
                        <div className='kpi-card kpi-open'>
                            <div className='kpi-value'>{data?.contracts?.open ?? '-'}</div>
                            <div className='kpi-label'>Niet Gepland</div>
                        </div>
                        <div className='kpi-card kpi-planned'>
                            <div className='kpi-value'>{data?.contracts?.planned ?? '-'}</div>
                            <div className='kpi-label'>Gepland</div>
                        </div>
                        <div className='kpi-card kpi-closed'>
                            <div className='kpi-value'>{data?.contracts?.closed ?? '-'}</div>
                            <div className='kpi-label'>Afgerond</div>
                        </div>
                        <div className='kpi-card kpi-total'>
                            <div className='kpi-value'>{data?.contracts?.total ?? '-'}</div>
                            <div className='kpi-label'>Totaal</div>
                        </div>
                    </div>
                </div>

                {/* KPI Row - Tasks & Relations */}
                <div className='kpi-section'>
                    <h3 className='section-title'>Opdrachten</h3>
                    <div className='kpi-row'>
                        <div className='kpi-card kpi-today'>
                            <div className='kpi-value'>{data?.tasks?.today ?? '-'}</div>
                            <div className='kpi-label'>Vandaag</div>
                        </div>
                        <div className='kpi-card kpi-week'>
                            <div className='kpi-value'>{data?.tasks?.thisWeek ?? '-'}</div>
                            <div className='kpi-label'>Deze Week</div>
                        </div>
                        <div className='kpi-card kpi-overdue'>
                            <div className='kpi-value'>{data?.tasks?.overdue ?? '-'}</div>
                            <div className='kpi-label'>Achterstallig</div>
                        </div>
                        <div className='kpi-card kpi-relations'>
                            <div className='kpi-value'>{data?.totalRelations ?? '-'}</div>
                            <div className='kpi-label'>Relaties</div>
                        </div>
                    </div>
                </div>

                {/* Charts row */}
                <div className='charts-row'>
                    <div className='chart-card'>
                        <h3 className='section-title'>Onderhoud per Maand ({new Date().getFullYear()})</h3>
                        {chartData.length > 0 ? (
                            <Chart dataSource={chartData} height={280}>
                                <CommonSeriesSettings argumentField='month' type='bar' barWidth={30} />
                                <Series valueField='count' name='Afgerond' color='#ef4444' />
                                <ArgumentAxis />
                                <ValueAxis allowDecimals={false} />
                                <Tooltip enabled />
                                <Legend visible={false} />
                            </Chart>
                        ) : (
                            <div className='chart-empty'>Geen data beschikbaar</div>
                        )}
                    </div>
                    <div className='chart-card'>
                        <h3 className='section-title'>Opdracht Status</h3>
                        {taskStatusData.length > 0 ? (
                            <Chart dataSource={taskStatusData} height={280}>
                                <CommonSeriesSettings argumentField='status' type='bar' barWidth={40} />
                                <Series valueField='count' name='Aantal' color='#ef4444' />
                                <ArgumentAxis />
                                <ValueAxis allowDecimals={false} />
                                <Tooltip enabled />
                                <Legend visible={false} />
                            </Chart>
                        ) : (
                            <div className='chart-empty'>Geen data beschikbaar</div>
                        )}
                    </div>
                </div>

                {/* Upcoming tasks */}
                <div className='table-card'>
                    <h3 className='section-title'>Komende Opdrachten</h3>
                    <DataGrid
                        dataSource={data?.upcomingTasks || []}
                        showBorders={false}
                        showRowLines
                        columnAutoWidth
                        rowAlternationEnabled
                        onRowClick={onTaskRowClick}
                        noDataText='Geen komende opdrachten'
                        hoverStateEnabled
                    >
                        <Column dataField='TaskDescription' caption='Omschrijving' />
                        <Column dataField='RelationName' caption='Relatie' />
                        <Column dataField='ActivityName' caption='Activiteit' />
                        <Column dataField='TaskStartDate' caption='Start' dataType='date' format='dd-MM-yyyy' width={120} />
                        <Column dataField='TaskEndDate' caption='Eind' dataType='date' format='dd-MM-yyyy' width={120} />
                        <Column dataField='Status' caption='Status' width={110} cellRender={(cellData) => (
                            <span className='status-badge' style={{ backgroundColor: STATUS_COLORS[cellData.value] || '#9e9e9e' }}>
                                {cellData.value}
                            </span>
                        )} />
                    </DataGrid>
                </div>

                {/* Overdue tasks */}
                {data?.overdueTasksList?.length > 0 && (
                    <div className='table-card table-card-warning'>
                        <h3 className='section-title'>Achterstallige Opdrachten</h3>
                        <DataGrid
                            dataSource={data.overdueTasksList}
                            showBorders={false}
                            showRowLines
                            columnAutoWidth
                            rowAlternationEnabled
                            onRowClick={onTaskRowClick}
                            hoverStateEnabled
                        >
                            <Column dataField='TaskDescription' caption='Omschrijving' />
                            <Column dataField='RelationName' caption='Relatie' />
                            <Column dataField='ActivityName' caption='Activiteit' />
                            <Column dataField='TaskStartDate' caption='Start' dataType='date' format='dd-MM-yyyy' width={120} />
                            <Column dataField='TaskEndDate' caption='Deadline' dataType='date' format='dd-MM-yyyy' width={120} />
                            <Column dataField='Status' caption='Status' width={110} cellRender={(cellData) => (
                                <span className='status-badge' style={{ backgroundColor: STATUS_COLORS[cellData.value] || '#9e9e9e' }}>
                                    {cellData.value}
                                </span>
                            )} />
                        </DataGrid>
                    </div>
                )}
            </div>
            <LoadPanel container='.content' visible={isLoading} position={{ of: '.layout-body' }} />
        </ScrollView>
    );
};
